import { supabase } from '../supabase';

/**
 * Dohvata kategorije partnera za određenu kompaniju.
 * Selektuje id, naziv_kategorije, opis — filtrirano po id_aziende,
 * sortirano po naziv_kategorije uzlazno.
 * @param {string} id_aziende - ID matične kompanije
 */
export const getKategorijePartnera = async (id_aziende) => {
  try {
    if (!id_aziende) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('partneri_kategorije')
      .select('id, naziv_kategorije, opis')
      .eq('id_aziende', id_aziende)
      .order('naziv_kategorije', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erorr!:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Kreira novu kategoriju partnera u bazi.
 * Prije unosa provjerava da li kategorija sa istim nazivom već postoji
 * za istu kompaniju (id_aziende). Ako postoji, vraća grešku.
 * @param {Object} params
 * @param {string} params.naziv_kategorije - Naziv nove kategorije (obavezno)
 * @param {string} [params.opis] - Opis kategorije (opcionalno)
 * @param {string} params.id_aziende - ID matične kompanije (obavezno)
 */
export const kreirajKategorijuPartnera = async ({ naziv_kategorije, opis, id_aziende }) => {
  try {
    if (!id_aziende) {
      throw new Error('ID kompanije je obavezan za kreiranje kategorije.');
    }
    if (!naziv_kategorije || !naziv_kategorije.trim()) {
      throw new Error('Category name is required!');
    }

    const trimmedNaziv = naziv_kategorije.trim();

    // Provjera duplikata: ista kategorija (case-insensitive) za istu kompaniju
    const { data: existing, error: checkError } = await supabase
      .from('partneri_kategorije')
      .select('id')
      .eq('id_aziende', id_aziende)
      .ilike('naziv_kategorije', trimmedNaziv)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existing) {
      throw new Error('A type with this name already exists!');
    }

    const { data, error } = await supabase
      .from('partneri_kategorije')
      .insert([{
        naziv_kategorije: trimmedNaziv,
        opis: opis?.trim() || null,
        id_aziende
      }])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error!:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Briše kategoriju partnera iz baze, po id kategorije i id_aziende
 * (dupli uslov osigurava da firma ne može obrisati kategoriju druge firme).
 * @param {string} id_kategorije - ID kategorije koja se briše
 * @param {string} id_aziende - ID matične kompanije (vlasnika kategorije)
 */
export const obrisiKategorijuPartnera = async (id_kategorije, id_aziende) => {
  try {
    if (!id_kategorije || !id_aziende) {
      throw new Error('ID kategorije i ID kompanije su obavezni za brisanje.');
    }

    const { error } = await supabase
      .from('partneri_kategorije')
      .delete()
      .eq('id', id_kategorije)
      .eq('id_aziende', id_aziende);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error!:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Registruje novog partnera u bazu podataka.
 * @param {Object} partnerData - Podaci iz forme
 */
export const kreirajPartnera = async (partnerData) => {
  try {
    const payload = {
      id_aziende: partnerData.id_aziende,
      id_kategorije: partnerData.id_kategorije || null,
      naziv_partnera: partnerData.naziv_partnera,
      tip_partnera: partnerData.tip_partnera || null,
      pib_jib: partnerData.pib_jib || null,
      pdv_broj: partnerData.pdv_broj || null,
      broj_registracije: partnerData.broj_registracije || null,
      adresa: partnerData.adresa || null,
      grad: partnerData.grad || null,
      postanski_broj: partnerData.postanski_broj || null,
      drzava: partnerData.drzava || null,
      email: partnerData.email || null,
      telefon: partnerData.telefon || null,
      web_stranica: partnerData.web_stranica || null,
      bankovni_racun: partnerData.bankovni_racun || null,
      naziv_banke: partnerData.naziv_banke || null,
      kontakt_osoba: partnerData.kontakt_osoba || null,
      kontakt_telefon: partnerData.kontakt_telefon || null,
      napomena: partnerData.napomena || null,
    };

    const { data, error } = await supabase
      .from('partneri')
      .insert([payload])
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error!:', error);
    return { success: false, error: error.message };
  }
};